import { supabase } from '../supabaseClient';
import { Session } from '@supabase/supabase-js';

// Subscription plan types
export enum SubscriptionPlan {
  FREE = 'free',
  STANDARD = 'standard',
  PREMIUM = 'premium'
}

// Limits for each plan
export const PLAN_LIMITS = {
  [SubscriptionPlan.FREE]: 30,
  [SubscriptionPlan.STANDARD]: 500,
  [SubscriptionPlan.PREMIUM]: 1500
};

// Prices for each plan (in USD)
export const PLAN_PRICES = {
  [SubscriptionPlan.FREE]: 0,
  [SubscriptionPlan.STANDARD]: 20,
  [SubscriptionPlan.PREMIUM]: 50
};

// Subscription Interface
export interface Subscription {
  id?: string;
  user_id: string;
  plan_type: SubscriptionPlan;
  analyses_limit: number;
  analyses_used: number;
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

// Subscription event listeners
type SubscriptionChangeCallback = (subscription: Subscription) => void;
const subscriptionListeners: Map<string, SubscriptionChangeCallback[]> = new Map();

/**
 * Subscribe to real-time changes in a user's subscription
 */
export function subscribeToUserSubscriptionChanges(
  userId: string,
  callback: SubscriptionChangeCallback
): () => void {
  if (!userId) {
    console.error('Cannot subscribe without userId');
    return () => {};
  }

  console.log(`Setting up real-time subscription for user ${userId}`);
  
  // Store the callback
  if (!subscriptionListeners.has(userId)) {
    subscriptionListeners.set(userId, []);
  }
  subscriptionListeners.get(userId)?.push(callback);
  
  // Set up the subscription if this is the first listener
  if (subscriptionListeners.get(userId)?.length === 1) {
    // Subscribe to changes in the 'subscriptions' table for this user
    const subscription = supabase
      .channel(`subscription-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          console.log('Subscription change detected via real-time:', payload);
          
          // Fetch the latest subscription data
          const latestSubscription = await getUserSubscription(userId);
          
          // Notify all listeners
          const listeners = subscriptionListeners.get(userId) || [];
          if (latestSubscription) {
            console.log('Notifying listeners of subscription change:', latestSubscription);
            listeners.forEach(listener => listener(latestSubscription));
          } else {
            console.log('Subscription change detected but no active subscription found');
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });
  }
  
  // Return unsubscribe function
  return () => {
    const listeners = subscriptionListeners.get(userId) || [];
    const index = listeners.indexOf(callback);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
    
    // If no listeners left, remove the Supabase subscription
    if (listeners.length === 0) {
      supabase.channel(`subscription-${userId}`).unsubscribe();
      subscriptionListeners.delete(userId);
      console.log(`Removed real-time subscription for user ${userId}`);
    }
  };
}

/**
 * Clean up multiple active subscriptions for a user - keeps only the most recent one
 */
export async function cleanupUserSubscriptions(userId: string): Promise<boolean> {
  try {
    console.log(`Cleaning up multiple subscriptions for user ${userId}`);
    
    // First, get all active subscriptions for this user
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching all subscriptions:', error);
      return false;
    }
    
    // If there are 0 or 1 subscriptions, no cleanup needed
    if (!data || data.length <= 1) {
      console.log(`No cleanup needed, found ${data ? data.length : 0} active subscriptions`);
      return true;
    }
    
    console.log(`Found ${data.length} active subscriptions, keeping the most recent one`);
    
    // Keep the first one (most recent) and deactivate all others
    const subscriptionsToDeactivate = data.slice(1);
    const ids = subscriptionsToDeactivate.map(s => s.id);
    
    // Deactivate all but the most recent subscription
    const { error: updateError } = await supabase
      .from('subscriptions')
      .update({ 
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .in('id', ids);
    
    if (updateError) {
      console.error('Error deactivating old subscriptions:', updateError);
      return false;
    }
    
    console.log(`Successfully deactivated ${ids.length} old subscriptions`);
    return true;
  } catch (error) {
    console.error('Error in cleanupUserSubscriptions:', error);
    return false;
  }
}

/**
 * Get a user's current subscription
 */
export async function getUserSubscription(userId: string): Promise<Subscription | null> {
  if (!userId) {
    console.error('getUserSubscription: userId is required');
    return null;
  }

  try {
    console.log(`Getting subscription for user: ${userId}`);
    
    // Simple solution to avoid caching - add a timestamp to the query
    const timestamp = new Date().getTime();
    
    // Make a direct query with a timestamp param to avoid caching
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('Error fetching subscription:', error);
      return null;
    }

    if (!data) {
      console.log('No active subscription found for user:', userId);
      return null;
    }

    console.log('Found active subscription:', data);

    // --- Self-Correction Logic --- 
    const subscription = data as Subscription;
    const expectedLimit = PLAN_LIMITS[subscription.plan_type];

    if (subscription.analyses_limit !== expectedLimit) {
      console.warn(`Subscription limit mismatch for user ${userId}. DB: ${subscription.analyses_limit}, Expected: ${expectedLimit}. Updating database.`);
      
      // Update the database record asynchronously (don't wait for it)
      supabase
        .from('subscriptions')
        .update({ analyses_limit: expectedLimit, updated_at: new Date().toISOString() })
        .eq('id', subscription.id)
        .then(({ error: updateError }) => {
          if (updateError) {
            console.error('Failed to auto-update subscription limit in DB:', updateError);
          } else {
            console.log('Successfully auto-updated subscription limit in DB.');
            // Optionally trigger a refresh notification
             window.dispatchEvent(new CustomEvent('usage-updated', { detail: { timestamp: Date.now() } }));
          }
        });

      // Immediately correct the limit in the returned object
      subscription.analyses_limit = expectedLimit;
    }
    // --- End Self-Correction Logic ---

    return subscription; // Return the potentially corrected subscription object
  } catch (error) {
    console.error('Error in getUserSubscription:', error);
    return null;
  }
}

/**
 * Create a new subscription for a user (Free tier by default)
 */
export async function createSubscription(
  userId: string, 
  planType: SubscriptionPlan = SubscriptionPlan.FREE
): Promise<Subscription | null> {
  try {
    console.log(`Creating new subscription for user ${userId} with plan ${planType}`);
    
    // First, deactivate any existing active subscriptions
    const { error: deactivateError } = await supabase
      .from('subscriptions')
      .update({ 
        is_active: false, 
        updated_at: new Date().toISOString() 
      })
      .eq('user_id', userId)
      .eq('is_active', true);
    
    if (deactivateError) {
      console.warn('Error deactivating existing subscriptions:', deactivateError);
      // Continue anyway, as we want to create a new subscription
    }
    
    const startDate = new Date();
    // Set end date to one month from now if paid plan
    let endDate = null;
    if (planType !== SubscriptionPlan.FREE) {
      const oneMonthLater = new Date(startDate);
      oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
      endDate = oneMonthLater.toISOString();
    }

    const newSubscription: Omit<Subscription, 'id' | 'created_at' | 'updated_at'> = {
      user_id: userId,
      plan_type: planType,
      analyses_limit: PLAN_LIMITS[planType],
      analyses_used: 0,
      start_date: startDate.toISOString(),
      end_date: endDate,
      is_active: true,
    };

    const { data, error } = await supabase
      .from('subscriptions')
      .insert([newSubscription])
      .select()
      .single();

    if (error) {
      console.error('Error creating subscription:', error);
      return null;
    }
    
    console.log(`Successfully created new ${planType} subscription:`, data?.id);
    return data;
  } catch (error) {
    console.error('Error creating subscription:', error);
    return null;
  }
}

/**
 * Upgrade a user's subscription to a new plan
 */
export async function upgradeSubscription(
  userId: string,
  newPlanType: SubscriptionPlan
): Promise<Subscription | null> {
  try {
    // Get current subscription
    const currentSubscription = await getUserSubscription(userId);

    // If no subscription exists, create a new one
    if (!currentSubscription) {
      return createSubscription(userId, newPlanType);
    }

    // Deactivate the current subscription
    const { error: deactivateError } = await supabase
      .from('subscriptions')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', currentSubscription.id);

    if (deactivateError) throw deactivateError;

    // Create a new subscription with the new plan
    return createSubscription(userId, newPlanType);
  } catch (error) {
    console.error('Error upgrading subscription:', error);
    return null;
  }
}

/**
 * Check if a user can analyze more resumes based on their subscription
 */
export async function checkUserCanAnalyze(userId: string): Promise<{
  canAnalyze: boolean;
  remainingAnalyses: number;
  currentPlan: SubscriptionPlan;
  isActive: boolean;
}> {
  console.log(`Checking if user ${userId} can analyze`);
  
  try {
    // First clean up any duplicate subscriptions to ensure we have valid data
    await cleanupUserSubscriptions(userId);
    
    // Get user's active subscription
    let subscription = await getUserSubscription(userId);
    
    // If no subscription exists, create a free one
    if (!subscription) {
      console.log('No active subscription found, creating a free one');
      subscription = await createSubscription(userId);
      if (!subscription) {
        throw new Error('Failed to create a free subscription');
      }
    }
    
    console.log(`Found subscription for ${userId}:`, subscription);
    
    // Check if subscription has expired
    const isExpired = !!subscription.end_date && new Date(subscription.end_date) < new Date();
    
    if (isExpired) {
      console.log('Subscription has expired');
      subscription.is_active = false;
      
      // Update the subscription in the database
      const { error } = await supabase
        .from('subscriptions')
        .update({ is_active: false })
        .eq('id', subscription.id);
        
      if (error) {
        console.error('Error updating expired subscription:', error);
      }
      
      // Create a new free subscription
      const newSubscription = await createSubscription(userId);
      if (newSubscription) {
        subscription = newSubscription;
      }
    }
    
    // Check if user can analyze based on remaining analyses
    const remainingAnalyses = Math.max(0, subscription.analyses_limit - subscription.analyses_used);
    const canAnalyze = subscription.is_active && remainingAnalyses > 0;
    
    console.log(`User ${userId} can analyze: ${canAnalyze}, remaining: ${remainingAnalyses}, plan: ${subscription.plan_type}`);
    
    return {
      canAnalyze,
      remainingAnalyses,
      currentPlan: subscription.plan_type,
      isActive: subscription.is_active
    };
  } catch (error) {
    console.error('Error in checkUserCanAnalyze:', error);
    
    // Return default free values in case of error
    return {
      canAnalyze: true,
      remainingAnalyses: PLAN_LIMITS[SubscriptionPlan.FREE],
      currentPlan: SubscriptionPlan.FREE,
      isActive: true
    };
  }
}

/**
 * Increment the analyses used count for a user
 */
export async function incrementAnalysesUsed(userId: string, count: number = 1): Promise<boolean> {
  try {
    console.log(`Incrementing subscription analyses_used for user ${userId} by ${count}`);
    
    // First, clean up any duplicate subscriptions
    await cleanupUserSubscriptions(userId);
    
    // Get the current subscription
    const subscription = await getUserSubscription(userId);
    
    if (!subscription) {
      console.error('Failed to get or create a subscription');
      return false;
    }
    
    // Update the subscription with new count
    console.log(`Current analyses_used: ${subscription.analyses_used}, adding ${count}`);
    
    // Make three attempts to update the subscription
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const { error } = await supabase
          .from('subscriptions')
          .update({ 
            analyses_used: subscription.analyses_used + count,
            updated_at: new Date().toISOString()
          })
          .eq('id', subscription.id);
        
        if (error) {
          console.error(`Attempt ${attempt}: Error incrementing analyses_used:`, error);
          if (attempt === 3) return false;
          // Wait a short time before retrying
          await new Promise(resolve => setTimeout(resolve, 300));
          continue;
        }
        
        console.log(`Successfully updated analyses_used to ${subscription.analyses_used + count}`);
        return true;
      } catch (updateError) {
        console.error(`Attempt ${attempt}: Exception incrementing analyses_used:`, updateError);
        if (attempt === 3) return false;
        // Wait a short time before retrying
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error in incrementAnalysesUsed:', error);
    return false;
  }
}

/**
 * Get information about all subscription plans
 */
export function getSubscriptionPlans() {
  return Object.values(SubscriptionPlan).map(planType => ({
    type: planType,
    name: getPlanName(planType),
    limit: PLAN_LIMITS[planType],
    price: PLAN_PRICES[planType]
  }));
}

/**
 * Get a human-readable name for a plan type
 */
function getPlanName(planType: SubscriptionPlan): string {
  switch (planType) {
    case SubscriptionPlan.FREE:
      return 'Free';
    case SubscriptionPlan.STANDARD:
      return 'Standard';
    case SubscriptionPlan.PREMIUM:
      return 'Premium';
    default:
      return 'Unknown';
  }
}

// Add a function to force-refresh subscription data
export async function forceRefreshSubscription(userId: string): Promise<Subscription | null> {
  console.log(`Force refreshing subscription data for user ${userId}`);
  
  try {
    // First clean up any duplicate subscriptions
    await cleanupUserSubscriptions(userId);
    
    // Make a direct query to the database to bypass all caching
    try {
      // Reset Supabase connection to ensure fresh data
      await supabase.rpc('pg_sleep', { seconds: 0.01 });
    } catch (e) {
      // Ignore any errors from this call
    }
    
    // Query directly with fresh connection
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
      
    if (error) {
      console.error('Error fetching refreshed subscription:', error);
      return null;
    }
    
    if (!data) {
      console.log('No active subscription found, creating a free one during refresh');
      return createSubscription(userId);
    }
    
    console.log('Refreshed subscription data:', data);
    
    // Explicitly trigger a real-time event to all listeners
    const listeners = subscriptionListeners.get(userId) || [];
    if (listeners.length > 0) {
      console.log(`Manually notifying ${listeners.length} listeners with fresh subscription data`);
      listeners.forEach(listener => listener(data as Subscription));
    }
    
    // Also create an event in the subscription_events table to force an update
    try {
      const { error: triggerError } = await supabase
        .from('subscription_events')
        .insert([{
          user_id: userId,
          event_type: 'refresh_subscription',
          subscription_id: data.id,
          created_at: new Date().toISOString()
        }]);
        
      if (triggerError) {
        console.warn('Error creating refresh event:', triggerError);
      }
    } catch (e) {
      console.warn('Error triggering real-time update:', e);
    }
    
    return data as Subscription;
  } catch (error) {
    console.error('Error force-refreshing subscription:', error);
    return null;
  }
} 